import { motion } from 'framer-motion';
import { useState, useEffect } from 'react';
import {
  Users,
  Shield,
  ShieldOff,
  Building2,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Search,
  Filter,
} from 'lucide-react';
import { adminApi, AdminUser } from '../services/api';

interface UserManagementSectionProps {
  isVisible: boolean;
  onClose: () => void;
  initialSearch?: string;
}

export default function UserManagementSection({
  isVisible,
  onClose,
  initialSearch = '',
}: UserManagementSectionProps) {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<AdminUser[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState<'all' | 'super' | 'regular'>('all');
  const [updatingUsers, setUpdatingUsers] = useState<Set<string>>(new Set());
  useEffect(() => {
    if (isVisible) setSearchTerm(initialSearch);
  }, [isVisible, initialSearch]);

  // Fetch users
  useEffect(() => {
    if (isVisible) {
      fetchUsers();
    }
  }, [isVisible]);

  // Filter users based on search and filter type
  useEffect(() => {
    let filtered = users;

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(
        (user) =>
          user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          user.email.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply type filter
    if (filterType === 'super') {
      filtered = filtered.filter((user) => user.superUser);
    } else if (filterType === 'regular') {
      filtered = filtered.filter((user) => !user.superUser);
    }

    setFilteredUsers(filtered);
  }, [users, searchTerm, filterType]);

  const fetchUsers = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await adminApi.getUsers();
      if (response.success && response.data) {
        setUsers(response.data);
      } else {
        setError('Failed to fetch users');
      }
    } catch (err) {
      setError('Error fetching users');
      console.error('Error fetching users:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleSuperUser = async (user: AdminUser) => {
    if (updatingUsers.has(user.id)) return;

    try {
      setUpdatingUsers((prev) => new Set([...prev, user.id]));

      const response = await adminApi.updateUserSuperStatus(user.id, !user.superUser);

      if (response.success) {
        // Update the user in the list
        setUsers((prevUsers) =>
          prevUsers.map((u) => (u.id === user.id ? { ...u, superUser: !u.superUser } : u))
        );
      } else {
        throw new Error(response.error || 'Failed to update user');
      }
    } catch (err) {
      console.error('Error updating super user status:', err);
      alert('Failed to update user privileges. Please try again.');
    } finally {
      setUpdatingUsers((prev) => {
        const newSet = new Set(prev);
        newSet.delete(user.id);
        return newSet;
      });
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getStatusIcon = (user: AdminUser) => {
    if (user.clerkData?.banned) {
      return (
        <span title="Banned">
          <XCircle className="h-4 w-4 text-red-500" />
        </span>
      );
    }
    if (user.clerkData?.locked) {
      return (
        <span title="Locked">
          <AlertTriangle className="h-4 w-4 text-yellow-500" />
        </span>
      );
    }
    if (user.clerkData?.emailVerified) {
      return (
        <span title="Verified">
          <CheckCircle2 className="h-4 w-4 text-green-500" />
        </span>
      );
    }
    return (
      <span title="Unverified">
        <XCircle className="h-4 w-4 text-gray-400" />
      </span>
    );
  };

  if (!isVisible) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-white rounded-lg shadow-xl w-full max-w-6xl h-full max-h-[90vh] flex flex-col"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <Users className="h-6 w-6 text-blue-600" />
            <div>
              <h2 className="text-xl font-semibold text-gray-900">User Management</h2>
              <p className="text-sm text-gray-600">
                Manage user accounts and super user privileges
              </p>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            ✕
          </button>
        </div>

        {/* Controls */}
        <div className="p-6 border-b border-gray-200 bg-gray-50">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search users by name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Filter */}
            <div className="flex items-center space-x-2">
              <Filter className="h-4 w-4 text-gray-400" />
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value as 'all' | 'super' | 'regular')}
                className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Users</option>
                <option value="super">Super Users</option>
                <option value="regular">Regular Users</option>
              </select>
            </div>
          </div>

          <div className="mt-4 flex items-center space-x-6 text-sm text-gray-600">
            <span>Total Users: {users.length}</span>
            <span>Super Users: {users.filter((u) => u.superUser).length}</span>
            <span>Showing: {filteredUsers.length}</span>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden">
          {isLoading ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <p className="text-gray-600">Loading users...</p>
              </div>
            </div>
          ) : error ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
                <p className="text-gray-600 mb-4">{error}</p>
                <button
                  onClick={fetchUsers}
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Try Again
                </button>
              </div>
            </div>
          ) : (
            <div className="overflow-auto h-full">
              <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4 p-6">
                {filteredUsers.map((user) => (
                  <motion.div
                    key={user.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                  >
                    {/* User Header */}
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center overflow-hidden">
                          {user.photo ? (
                            <img
                              src={user.photo}
                              alt={user.name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <Users className="h-5 w-5 text-gray-400" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-medium text-gray-900 truncate">{user.name}</h3>
                          <p className="text-sm text-gray-500 truncate">{user.email}</p>
                        </div>
                      </div>
                      {getStatusIcon(user)}
                    </div>

                    {/* User Details */}
                    <div className="space-y-2 text-sm text-gray-600 mb-4">
                      <div className="flex items-center justify-between">
                        <span>Joined:</span>
                        <span>{formatDate(user.createdAt)}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span>Companies:</span>
                        <span className="flex items-center">
                          <Building2 className="h-3 w-3 mr-1" />
                          {user.companyMemberships}
                        </span>
                      </div>
                      {user.clerkData?.lastSignInAt && (
                        <div className="flex items-center justify-between">
                          <span>Last Login:</span>
                          <span>{formatDate(user.clerkData.lastSignInAt.toString())}</span>
                        </div>
                      )}
                      {user.title && (
                        <div className="flex items-center justify-between">
                          <span>Title:</span>
                          <span className="truncate">{user.title}</span>
                        </div>
                      )}
                    </div>

                    {/* Clerk Status */}
                    {user.clerkData && (
                      <div className="mb-4 p-2 bg-gray-50 rounded border text-xs">
                        <div className="flex items-center justify-between">
                          <span>Email Verified:</span>
                          <span
                            className={
                              user.clerkData.emailVerified ? 'text-green-600' : 'text-red-600'
                            }
                          >
                            {user.clerkData.emailVerified ? 'Yes' : 'No'}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span>2FA Enabled:</span>
                          <span
                            className={
                              user.clerkData.twoFactorEnabled ? 'text-green-600' : 'text-gray-600'
                            }
                          >
                            {user.clerkData.twoFactorEnabled ? 'Yes' : 'No'}
                          </span>
                        </div>
                      </div>
                    )}

                    {/* Super User Toggle */}
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center space-x-2">
                        {user.superUser ? (
                          <Shield className="h-4 w-4 text-red-600" />
                        ) : (
                          <ShieldOff className="h-4 w-4 text-gray-400" />
                        )}
                        <span className="text-sm font-medium">
                          {user.superUser ? 'Super User' : 'Regular User'}
                        </span>
                      </div>

                      <button
                        onClick={() => toggleSuperUser(user)}
                        disabled={updatingUsers.has(user.id)}
                        className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                          user.superUser
                            ? 'bg-red-100 text-red-700 hover:bg-red-200'
                            : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                        } ${updatingUsers.has(user.id) ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        {updatingUsers.has(user.id) ? (
                          <div className="flex items-center space-x-1">
                            <div className="animate-spin h-3 w-3 border border-current border-t-transparent rounded-full"></div>
                            <span>Updating...</span>
                          </div>
                        ) : user.superUser ? (
                          'Revoke'
                        ) : (
                          'Grant'
                        )}
                      </button>
                    </div>
                  </motion.div>
                ))}
              </div>

              {filteredUsers.length === 0 && !isLoading && (
                <div className="flex items-center justify-center h-64">
                  <div className="text-center text-gray-500">
                    <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>No users found</p>
                    {searchTerm && (
                      <p className="text-sm mt-1">Try adjusting your search or filters</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}
